import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import defaultThumbnail from "@assets/images/default-thumbnail.jpg";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Course } from "@domain/lms/course/course";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { ImageVersions } from "@domain/media/dtos";
import { useHover } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CourseIcon } from "@ui/components/icons";
import { UserView } from "@ui/components/profile/userView";
import { SubAuthor } from "@ui/components/subAuthor";
import { BookmarkToggle } from "./bookmarkToggle";
import { CourseDetails } from "./courseDetails";
import { CourseProviderIcon } from "./courseProviderIcon";

interface CourseThumbnailSmallProps extends React.HTMLAttributes<HTMLDivElement> {
	course: Course;
	bannerHeight?: number;
}
export const CourseThumbnailSmall: React.FC<CourseThumbnailSmallProps> = ({ course, bannerHeight = 255 }) => {
	const { banner, category, provider, author, isFavorite } = course;
	const { courseService } = useServices();
	const displayedAuthor = getDisplayedAuthor(course);
	const subAuthor = displayedAuthor !== author ? author : undefined;
	const currentProfile = useCurrentProfile();
	const isAuthor = displayedAuthor.id === currentProfile?.id || subAuthor?.id === currentProfile?.id;
	const { ref, isHovered } = useHover();

	return (
		<Content ref={ref}>
			<Thumbnail>
				<Link to={Path.Lms.ReferenceCourse(course)}>
					<StyledBackgroundImage
						image={banner}
						backdrop
						height={bannerHeight}
						defaultUrl={!banner ? defaultThumbnail : undefined}
						size={ImageVersions.MEDIUM}
						isHovered={isHovered}
						insideRadius={true}
					/>
				</Link>
				<Infos>
					<CourseProviderIcon provider={provider} />
					<CourseTitle>{course.title}</CourseTitle>
					<StyledUserView
						pictureSize={"S"}
						user={displayedAuthor}
						alignStart={!!subAuthor}
						renderBottomAccessory={subAuthor ? () => <SubAuthor author={subAuthor} /> : undefined}
					/>
				</Infos>
				{!!category && category !== "OTHERS" && (
					<Category
						isTitleTruncated={true}
						renderIcons={() => <CourseIcon color={theme.colors.white} />}
						category={category}
						isSmall={true}
					/>
				)}
				{!isAuthor && (
					<StyledBookmark
						checked={isFavorite}
						onClick={async () => {
							await courseService.toggleFavorite({ ...course });
						}}
					/>
				)}
			</Thumbnail>
			<StyledCourseDetails course={course} hideLanguage />
		</Content>
	);
};

const Content = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	border-radius: 12px;
	cursor: pointer;
	overflow: hidden;
	background-color: ${({ theme }) => theme.colors.backgroundPrimary};

	@media (max-width: 900px) {
		padding-top: 0;
	}
	height: 309px;
`;

const Thumbnail = styled(RoundedBlock)`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-width: 0px;

	> a {
		width: 100%;
		height: 100%;
	}
`;

const StyledBackgroundImage = styled(BackgroundImage)<{ height?: number }>`
	height: ${({ height = 215 }) => height}px;
`;

const Infos = styled.div`
	${gap(8)};
	${({ theme }) => theme.textStyles.text};
	color: ${({ theme }) => theme.colors.white};
	padding: 15px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-end;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
`;

const CourseTitle = styled.div`
	overflow: hidden;
	display: -webkit-box;
	text-overflow: ellipsis;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
`;

const StyledCourseDetails = styled(CourseDetails)`
	${({ theme }) => theme.textStyles.text};
	font-weight: 700 !important;
	padding: 12px 15px;
`;

const StyledUserView = styled(UserView)`
	color: ${({ theme }) => theme.colors.white};
`;

const Category = styled(CategoryLabel)`
	position: absolute;
	left: 15px;
	top: 15px;
	max-width: 73%;
`;

const StyledBookmark = styled(BookmarkToggle)`
	position: absolute;
	top: 15px;
	right: 15px;
`;
